import React, { useState, useEffect } from "react";
import { orchestratorApi } from "../api/orchestrator";

interface DeadLetterEvent {
  event_id: string;
  event_type: string;
  entity_id: string;
  chat_id: string;
  error_message: string;
  retry_count: number;
  created_at: string;
  event_data: any;
}

interface DeadLetterStats {
  total: number;
  byEntity: Array<{ entity_id: string; count: number }>;
  byEventType: Array<{ event_type: string; count: number }>;
  last24h: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const DeadLetterQueue: React.FC = () => {
  const [events, setEvents] = useState<DeadLetterEvent[]>([]);
  const [stats, setStats] = useState<DeadLetterStats | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<DeadLetterEvent | null>(null);

  const fetchEvents = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/admin/dead-letter?page=${page}&limit=50`, {
        headers: authHeaders
      });
      if (!response.ok) throw new Error("Failed to fetch dead-letter events");
      const data = await response.json();
      setEvents(data.data.events);
      setPagination(data.data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch("/api/admin/dead-letter/stats", {
        headers: authHeaders
      });
      if (!response.ok) throw new Error("Failed to fetch stats");
      const data = await response.json();
      setStats(data.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  const retryEvent = async (eventId: string) => {
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/admin/dead-letter/${eventId}/retry`, {
        method: "POST",
        headers: authHeaders
      });
      if (!response.ok) throw new Error("Failed to retry event");
      const data = await response.json();
      alert(data.message || "Event re-enqueued for retry");
      await fetchEvents(pagination.page);
      await fetchStats();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to retry event");
    }
  };

  const deleteEvent = async (eventId: string) => {
    if (!confirm("Are you sure you want to delete this event? This cannot be undone.")) {
      return;
    }
    try {
      const authHeaders = await orchestratorApi.getAuthHeaders();
      const response = await fetch(`/api/admin/dead-letter/${eventId}`, {
        method: "DELETE",
        headers: authHeaders
      });
      if (!response.ok) throw new Error("Failed to delete event");
      const data = await response.json();
      alert(data.message || "Event deleted successfully");
      await fetchEvents(pagination.page);
      await fetchStats();
      setSelectedEvent(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete event");
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, []);

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatEventData = (event: DeadLetterEvent) => {
    try {
      return JSON.stringify(event.event_data, null, 2);
    } catch {
      return String(event.event_data);
    }
  };

  return (
    <div className="dlq-section">
      <div className="dlq-header">
        <h2>⚠️ Dead Letter Queue</h2>
        <p className="section-description">
          Manage failed events that could not be processed. Review errors, retry events, or delete permanently.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="dlq-stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Dead-Lettered</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.last24h}</div>
            <div className="stat-label">Last 24 Hours</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.byEventType.length}</div>
            <div className="stat-label">Event Types</div>
          </div>
        </div>
      )}

      {/* Events Table */}
      <div className="dlq-events-section">
        <div className="section-header">
          <h3>Dead-Lettered Events</h3>
          <button onClick={() => fetchEvents(pagination.page)} className="btn-secondary">
            🔄 Refresh
          </button>
        </div>

        {error && (
          <div className="error-banner">
            ❌ {error}
          </div>
        )}

        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✨</div>
            <h3>No Dead-Lettered Events</h3>
            <p>All events are processing successfully!</p>
          </div>
        ) : (
          <>
            <div className="events-table-wrapper">
              <table className="events-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Event Type</th>
                    <th>Entity</th>
                    <th>Chat ID</th>
                    <th>Error</th>
                    <th>Retries</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.event_id}>
                      <td>
                        {getRelativeTime(event.created_at)}
                      </td>
                      <td>
                        <span className="event-type-badge">{event.event_type}</span>
                      </td>
                      <td>{event.entity_id}</td>
                      <td className="chat-id-cell">
                        {event.chat_id}
                      </td>
                      <td className="error-cell">
                        {event.error_message?.substring(0, 50)}
                        {event.error_message?.length > 50 && "..."}
                      </td>
                      <td>{event.retry_count}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => setSelectedEvent(event)}
                            className="btn-view"
                            title="View details"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => retryEvent(event.event_id)}
                            className="btn-retry"
                            title="Retry event"
                          >
                            🔄
                          </button>
                          <button
                            onClick={() => deleteEvent(event.event_id)}
                            className="btn-delete"
                            title="Delete event"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => fetchEvents(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn-page"
                >
                  ← Previous
                </button>
                <span className="page-info">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => fetchEvents(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="btn-page"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="modal-content dlq-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Event Details</h3>
              <button onClick={() => setSelectedEvent(null)} className="modal-close">✕</button>
            </div>
            <div className="modal-body dlq-modal-body">
              <div className="detail-row">
                <span className="detail-label">Event ID:</span>
                <span className="detail-value">{selectedEvent.event_id}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Event Type:</span>
                <span className="detail-value">{selectedEvent.event_type}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Entity ID:</span>
                <span className="detail-value">{selectedEvent.entity_id}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Chat ID:</span>
                <span className="detail-value">{selectedEvent.chat_id || "N/A"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created:</span>
                <span className="detail-value">{new Date(selectedEvent.created_at).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Retry Count:</span>
                <span className="detail-value">{selectedEvent.retry_count}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Error:</span>
                <span className="detail-value error-text">{selectedEvent.error_message}</span>
              </div>
              <div className="detail-row full-width">
                <span className="detail-label">Event Data:</span>
                <pre className="event-data-preview">{formatEventData(selectedEvent)}</pre>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => retryEvent(selectedEvent.event_id)} className="btn-primary">
                🔄 Retry Event
              </button>
              <button onClick={() => deleteEvent(selectedEvent.event_id)} className="btn-danger">
                🗑️ Delete
              </button>
              <button onClick={() => setSelectedEvent(null)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .dlq-section {
          padding: 20px;
        }

        .dlq-header {
          margin-bottom: 24px;
        }

        .dlq-header h2 {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .section-description {
          color: #666;
          font-size: 14px;
        }

        .dlq-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 16px;
          text-align: center;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 600;
          color: #dc3545;
        }

        .stat-label {
          font-size: 12px;
          color: #666;
          margin-top: 4px;
        }

        .dlq-events-section {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .section-header h3 {
          font-size: 18px;
          font-weight: 600;
        }

        .btn-secondary {
          padding: 8px 16px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .error-banner {
          background: #f8d7da;
          color: #721c24;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 16px;
        }

        .loading-state, .empty-state {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .events-table-wrapper {
          overflow-x: auto;
        }

        .events-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .events-table th {
          background: #f8f9fa;
          padding: 10px;
          text-align: left;
          font-weight: 600;
          border-bottom: 2px solid #dee2e6;
        }

        .events-table td {
          padding: 10px;
          border-bottom: 1px solid #e9ecef;
        }

        .event-type-badge {
          background: #e9ecef;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-family: monospace;
        }

        .chat-id-cell {
          font-family: monospace;
          font-size: 11px;
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .error-cell {
          color: #dc3545;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .action-buttons {
          display: flex;
          gap: 4px;
        }

        .btn-view, .btn-retry, .btn-delete {
          padding: 4px 8px;
          border: 1px solid #dee2e6;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .btn-view:hover {
          background: #e9ecef;
        }

        .btn-retry:hover {
          background: #d4edda;
          border-color: #c3e6cb;
        }

        .btn-delete:hover {
          background: #f8d7da;
          border-color: #f5c6cb;
        }

        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          margin-top: 16px;
        }

        .btn-page {
          padding: 6px 12px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .btn-page:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          max-width: 600px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
        }

        .dlq-modal {
          max-width: 700px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border-bottom: 1px solid #e9ecef;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
        }

        .dlq-modal-body {
          padding: 16px;
        }

        .detail-row {
          display: flex;
          margin-bottom: 12px;
        }

        .detail-row.full-width {
          flex-direction: column;
        }

        .detail-label {
          font-weight: 600;
          min-width: 120px;
          color: #495057;
        }

        .detail-value {
          color: #212529;
          word-break: break-word;
        }

        .error-text {
          color: #dc3545;
        }

        .event-data-preview {
          background: #f8f9fa;
          padding: 12px;
          border-radius: 4px;
          overflow-x: auto;
          font-size: 11px;
          font-family: monospace;
          max-height: 200px;
          overflow-y: auto;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 16px;
          border-top: 1px solid #e9ecef;
        }

        .btn-primary {
          padding: 8px 16px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .btn-danger {
          padding: 8px 16px;
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};
